import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatISOStringToReadable } from '../../../../utils/dateUtils';
import { BOOKING_STATUS_OPTIONS, MODAL_MODES } from '../../../../constants/enums';
import { translateTableStatus } from '../../../../utils/stranslateUltils';
import { getAvailableTables } from '../../../../api/tableAPI';
import { toISO, getLocalDateString } from '../../../../utils/dateUtils';
import { translateMembershipLevel } from '../../../../utils/stranslateUltils';
import BaseModal from '../Base/BaseModal';

const BookingModal = ({ booking, mode, onSave, onDelete, onClose }) => {
  const { t } = useTranslation();
  const [editedBooking, setEditedBooking] = useState(booking);
  const [validationErrors, setValidationErrors] = useState([]);

  // Separate state for first and last name inputs in edit mode
  const [nameInputs, setNameInputs] = useState({
    firstName: '',
    lastName: ''
  });

  // Separate state for date and time inputs in edit mode
  const [dateTimeInputs, setDateTimeInputs] = useState({
    date: null,
    time: ''
  });

  // Table selection states for edit mode
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState([]);

  useEffect(() => {
    setEditedBooking(booking);
    setValidationErrors([]);

    // Initialize name inputs from booking data
    if (booking.customer?.customerName) {
      const nameParts = booking.customer.customerName.split(' ');
      setNameInputs({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || ''
      });
    }

    // Initialize date/time inputs from booking data
    if (booking.bookingAt) {
      const bookingDate = new Date(booking.bookingAt);
      const time = bookingDate.toTimeString().slice(0, 5);
      setDateTimeInputs({ date: bookingDate, time });
    }

    // Initialize selected tables and sync with editedBooking
    if (booking.tables) {
      const tableIds = booking.tables.map(table => table.tableId);
      setSelectedTableIds(tableIds);
      // Ensure editedBooking has the tableIds property properly initialized
      setEditedBooking(prev => ({
        ...prev,
        tableIds: tableIds
      }));
    }
  }, [booking]);

  // Generate time options with 30-minute intervals
  const timeOptions = useMemo(() => {
    const times = [];
    for (let hour = 9; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeString);
      }
    }
    return times;
  }, []);

  // Fetch available tables
  const fetchTables = useCallback(async (date) => {
    try {
      setTablesLoading(true);
      const dateString = getLocalDateString(date);
      const response = await getAvailableTables({ date: dateString });
      // Handle different response structures
      let tableData = [];
      if (response && response.data) {
        tableData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        tableData = response;
      }
      
      // Include currently selected tables even if they might not be "available"
      const currentTables = booking.tables || [];
      
      // Merge available tables with current tables
      const mergedTables = [...tableData];
      currentTables.forEach(currentTable => {
        if (!mergedTables.find(t => t.tableId === currentTable.tableId)) {
          mergedTables.push(currentTable);
        }
      });
      
      setTables(mergedTables);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setValidationErrors(prev => [...prev, 'Failed to load available tables']);
    } finally {
      setTablesLoading(false);
    }
  }, [booking.tables]);

  // Fetch tables when date changes in edit mode
  useEffect(() => {
    if (mode === MODAL_MODES.EDIT && dateTimeInputs.date) {
      fetchTables(dateTimeInputs.date);
    }
  }, [dateTimeInputs.date, mode, fetchTables]);

  const handleTableSelection = (tableId) => {
    const newSelectedTableIds = selectedTableIds.includes(tableId)
      ? selectedTableIds.filter(id => id !== tableId)
      : [...selectedTableIds, tableId];
    
    setSelectedTableIds(newSelectedTableIds);
    setEditedBooking(prev => ({
      ...prev,
      tableIds: newSelectedTableIds
    }));

    // Clear validation errors when user selects tables
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  
  const validateBooking = (booking) => {
    const errors = [];

    if (!booking.customer?.customerName?.trim()) {
      errors.push(t('admin.booking_modal.validation.customer_name'));
    }

    if (!booking.customer?.phoneNumber?.trim()) {
      errors.push(t('admin.booking_modal.validation.phone'));
    }

    if (!booking.numGuests || booking.numGuests < 1) {
      errors.push(t('admin.booking_modal.validation.guests'));
    }

    if (!booking.bookingAt) {
      errors.push(t('admin.booking_modal.validation.datetime'));
    }

    // Fixed validation: check editedBooking.tableIds instead of booking.tableIds
    if (mode === MODAL_MODES.EDIT && (!booking.tableIds || booking.tableIds.length === 0)) {
      errors.push(t('admin.booking_modal.validation.tables'));
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditedBooking(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditedBooking(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleNameChange = (field, value) => {
    const newNameInputs = {
      ...nameInputs,
      [field]: value
    };
    setNameInputs(newNameInputs);

    // Update customerName in editedBooking
    const fullName = `${newNameInputs.firstName} ${newNameInputs.lastName}`.trim();
    handleInputChange('customer.customerName', fullName);

    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleDateTimeChange = (field, value) => {
    const newDateTimeInputs = {
      ...dateTimeInputs,
      [field]: value,
    };
    setDateTimeInputs(newDateTimeInputs);

    const { date, time } = newDateTimeInputs;
    if (date && time) {
      const bookingAtValue = toISO(date, time);
      handleInputChange('bookingAt', bookingAtValue);
    }

    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSave = () => {
    const validation = validateBooking(editedBooking);
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }
    onSave(editedBooking);
  };

  const renderViewContent = () => (
    <>
      <div className="detail-section">
        <h3>{t('admin.booking_modal.view.booking_info')}</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.booking_id')}:</label>
            <span>{booking.bookingId}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.status')}:</label>
            <span className={`status-badge status-${booking.status.toLowerCase()}`}>
              {t(`status.booking.${booking.status.toLowerCase()}`)}
            </span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.booking_date')}:</label>
            <span>{formatISOStringToReadable(booking.bookingAt)}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.num_guests')}:</label>
            <span>{booking.numGuests}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.note')}:</label>
            <span>{booking.note || t('admin.booking_modal.view.no_notes')}</span>
          </div>
        </div>
      </div>

      <div className="detail-section">
        <h3>{t('admin.booking_modal.view.customer_info')}</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.customer_name')}:</label>
            <span>{booking.customer.customerName}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.customer_phone')}:</label>
            <span>{booking.customer.phoneNumber}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.customer_email')}:</label>
            <span>{booking.customer.email || t('admin.booking_modal.view.no_email')}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.membership')}:</label>
            <span>{translateMembershipLevel(t,booking.customer.membershipLevel)}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.loyalty_points')}:</label>
            <span>{booking.customer.loyaltyPoints}</span>
          </div>
        </div>
      </div>

      <div className="detail-section">
        <h3>{t('admin.booking_modal.view.table_info')}</h3>
        <div className="item-list">
          {booking.tables.map(table => (
            <div key={table.tableId} className="list-item">
              <span className="item-name">{table.tableName}</span>
              <span className="item-description">
                {t('admin.booking_modal.view.table.capacity')}: {table.capacity}
              </span>
              <span className={`status-badge status-${table.status.toLowerCase().replace(/_/g, "-")}`}>
                {t(`status.table.${table.status.toLowerCase()}`)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="detail-section">
        <h3>{t('admin.booking_modal.view.timestamps')}</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.created_at')}:</label>
            <span>{formatISOStringToReadable(booking.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.booking_modal.view.updated_at')}:</label>
            <span>{formatISOStringToReadable(booking.updatedAt)}</span>
          </div>
        </div>
      </div>
    </>
  );

  const renderEditContent = () => (
    <>
      <div className="edit-section">
        <h3>{t('admin.booking_modal.edit.title')}</h3>
        <div className="form-grid">
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.first_name')} *:</label>
            <input
              type="text"
              value={nameInputs.firstName}
              readOnly
              onChange={(e) => handleNameChange('firstName', e.target.value)}
              className="form-input"
              placeholder={t('admin.booking_modal.edit.placeholders.first_name')}
            />
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.last_name')} *:</label>
            <input
              type="text"
              value={nameInputs.lastName}
              readOnly
              onChange={(e) => handleNameChange('lastName', e.target.value)}
              className="form-input"
              placeholder={t('admin.booking_modal.edit.placeholders.last_name')}
            />
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.phone')} *:</label>
            <input
              type="text"
              value={editedBooking.customer.phoneNumber}
              readOnly
              onChange={(e) => handleInputChange('customer.phoneNumber', e.target.value)}
              className="form-input"
              placeholder={t('admin.booking_modal.edit.placeholders.phone')}
            />
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.email')} *:</label>
            <input
              type="email"
              value={editedBooking.customer.email || ''}
              readOnly
              onChange={(e) => handleInputChange('customer.email', e.target.value)}
              className="form-input"
              placeholder={t('admin.booking_modal.edit.placeholders.email')}
            />
          </div>
        </div>
      </div>

      <div className="edit-section">
        <h3>{t('admin.booking_modal.edit.title')}</h3>
        <div className="form-grid">
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.status')}:</label>
            <select
              value={editedBooking.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="form-input"
            >
              {BOOKING_STATUS_OPTIONS.slice(1).map(option => (
                <option key={option.value} value={option.value}>
                  {t(`status.booking.${option.value.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.num_guests')} *:</label>
            <input
              type="number"
              value={editedBooking.numGuests}
              onChange={(e) => handleInputChange('numGuests', parseInt(e.target.value) || 0)}
              className="form-input"
              min="1"
            />
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.date')} *:</label>
            <DatePicker
              selected={dateTimeInputs.date}
              onChange={(date) => handleDateTimeChange('date', date)}
              minDate={new Date()}
              dateFormat="yyyy-MM-dd"
              className="form-input"
              placeholderText={t('admin.booking_modal.edit.placeholders.date')}
            />
          </div>
          <div className="form-item">
            <label>{t('admin.booking_modal.edit.time')} *:</label>
            <select
              value={dateTimeInputs.time}
              onChange={(e) => handleDateTimeChange('time',e.target.value)}
              className="form-input"
            >
              <option value={dateTimeInputs.time}>{dateTimeInputs.time}</option>
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
          <div className="form-item full-width">
            <label>{t('admin.booking_modal.edit.note')}:</label>
            <textarea
              value={editedBooking.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              className="form-input"
              rows="3"
              placeholder={t('admin.booking_modal.edit.placeholders.note')}
            />
          </div>
        </div>
      </div>

      {/* Table Selection Section */}
      <div className="edit-section">
        <h3>{t('admin.booking_modal.edit.table_selection')}</h3>
        {dateTimeInputs.date ? (
          <div className="table-selection-section">
            {tablesLoading ? (
              <div className="loading-message">{t('admin.booking_modal.edit.loading_tables')}</div>
            ) : tables.length > 0 ? (
              <div className="available-tables">
                <label className="section-label">{t('admin.booking_modal.edit.available_tables')} *:</label>
                <div className="table-options-grid">
                  {tables.map((table) => (
                    <div
                      key={table.tableId}
                      className={`table-option-card ${selectedTableIds.includes(table.tableId) ? 'selected' : ''}`}
                      onClick={() => handleTableSelection(table.tableId)}
                    >
                      <div className="table-card-header">
                        <span className="table-name">{table.tableName}</span>
                        <div className="table-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedTableIds.includes(table.tableId)}
                            onChange={() => handleTableSelection(table.tableId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="table-card-details">
                        <span className="table-detail">{t('admin.booking_modal.view.table.id')}: {table.tableId}</span>
                        <span className="table-detail">{t('admin.booking_modal.view.table.capacity')}: {table.capacity}</span>
                        <span className="table-detail">{t('admin.booking_modal.view.table.status')}: {translateTableStatus(t,table.status)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {selectedTableIds.length > 0 && (
                  <div className="selected-tables-info">
                    <span className="selected-count">
                      {t('admin.booking_modal.edit.selected_count', {
                        count: selectedTableIds.length,
                        plural: selectedTableIds.length > 1 ? 's' : ''
                      })}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-tables-message">
                {t('admin.booking_modal.edit.no_tables')}
              </div>
            )}
          </div>
        ) : (
          <div className="select-datetime-message">
            {t('admin.booking_modal.edit.select_date')}
          </div>
        )}
      </div>
    </>
  );

  const deleteConfirmation = (
    <>
      <h3>{t('admin.booking_modal.delete.confirmation')}</h3>
      <p>
        {t('admin.booking_modal.delete.booking_info', {
          name: booking.customer.customerName,
          id: booking.bookingId
        })}
      </p>
    </>
  );

  const getTitle = () => {
    switch (mode) {
      case MODAL_MODES.VIEW:
        return t('admin.booking_modal.view.title');
      case MODAL_MODES.EDIT:
        return t('admin.booking_modal.edit.title');
      case MODAL_MODES.DELETE:
        return t('admin.booking_modal.delete.title');
      default:
        return t('admin.booking_modal.title');
    }
  };

  const getContent = () => {
    switch (mode) {
      case MODAL_MODES.VIEW:
        return renderViewContent();
      case MODAL_MODES.EDIT:
        return renderEditContent();
      case MODAL_MODES.DELETE:
        return null; 
      default:
        return null;
    }
  };

  return (
    <BaseModal
      title={getTitle()}
      mode={mode}
      onClose={onClose}
      onSave={handleSave}
      onDelete={onDelete}
      validationErrors={validationErrors}
      saveButtonText={t('admin.base_modal.save_changes')}
      saveButtonDisabled={selectedTableIds.length === 0}
      deleteConfirmation={deleteConfirmation}
    >
      {getContent()}
    </BaseModal>
  );
};

export default BookingModal;